import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/api/queryKeys';

/** Thrown rather than written locally — see the hook's doc comment. */
const NOT_IMPLEMENTED = 'Gateway policy control is not available yet.';

interface ToggleServerInput {
  serverId: string;
  blocked: boolean;
}

interface ToggleToolInput {
  serverId: string;
  toolId: string;
  blocked: boolean;
}

/**
 * Gateway-control toggle mutations (KIR-3940), currently INERT (KIR-5042).
 *
 * There is no gateway policy route on the backend — `routes/organizations/` has
 * no gateway file, and the PRs that add one are still open. Until there is,
 * these reject rather than write: the previous version called an in-memory
 * fixture store, so the switch moved, the table invalidated, and no server ever
 * heard about it. Both controls that call these are now rendered disabled with
 * a tooltip saying so, which makes this the belt to that braces — a caller that
 * reaches a mutation anyway gets an error, not a silent local edit.
 *
 * The `useMutation` shape is deliberately unchanged, so the slice that adds
 * `PUT /organizations/:orgId/gateway/{servers,tools}/:id/policy` replaces the
 * two `mutationFn` bodies and nothing else — columns, expansion and the
 * confirmation modal all keep their current wiring.
 */
export function useGatewayControlToggle(): {
  toggleServer: UseMutationResult<void, Error, ToggleServerInput>;
  toggleTool: UseMutationResult<void, Error, ToggleToolInput>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.mcp.all });
  };

  const toggleServer = useMutation<void, Error, ToggleServerInput>({
    mutationFn: () => Promise.reject(new Error(NOT_IMPLEMENTED)),
    onSuccess: invalidate,
  });

  const toggleTool = useMutation<void, Error, ToggleToolInput>({
    mutationFn: () => Promise.reject(new Error(NOT_IMPLEMENTED)),
    onSuccess: invalidate,
  });

  return { toggleServer, toggleTool };
}
